<script>
  // reference https://github.com/noeldelgado/gemini-scrollbar/blob/master/index.js

  import { addResizeListener, removeResizeListener } from '../../utils/resize-event';
  import scrollbarWidth from '../../utils/scrollbar-width';
  import { toObject } from '../../utils/util';
  import Bar from './bar';
  import ZmSticky from '../sticky';
  import { createScroll, raf } from '../../utils/scroll';
  import { isInView, getOffsetTop } from '../../utils/dom';

  /* istanbul ignore next */
  export default {
    name: 'ZmScrollbar',

    components: { ZmSticky, Bar },

    props: {
      native: Boolean,
      wrapStyle: [Array, String],
      wrapClass: [Object, String],
      viewClass: [Object, String],
      viewStyle: [Object, String],
      noresize: Boolean, // 如果 container 尺寸不会发生变化，最好设置它可以优化性能
      tag: {
        type: String,
        default: 'div'
      },
      sticky: {
        type: Boolean,
        default: false
      }
    },

    data() {
      return {
        sizeWidth: '0',
        sizeHeight: '0',
        moveX: 0,
        moveY: 0,
        isFocus: false
      };
    },

    computed: {
      wrap() {
        return this.$refs.wrap;
      },
      enabled() {
        return this.sticky && this.sizeWidth !== '' && this.sizeWidth !== '0';
      },
      appendCls() {
        return this.isFocus ? 'zm-scrollbar zm-scrollbar__focus' : '';
      }
    },

    provide() {
      return {
        scrollbar: this
      };
    },

    render(h) {
      const gutter = scrollbarWidth();
      let style = this.wrapStyle;

      if (gutter) {
        const gutterWith = `-${gutter}px`;
        const gutterStyle = `margin-bottom: ${gutterWith}; margin-right: ${gutterWith};`;

        if (Array.isArray(this.wrapStyle)) {
          style = toObject(this.wrapStyle);
          style.marginRight = style.marginBottom = gutterWith;
        } else if (typeof this.wrapStyle === 'string') {
          style += gutterStyle;
        } else {
          style = gutterStyle;
        }
      }
      const view = h(this.tag, {
        class: ['zm-scrollbar__view', this.viewClass],
        style: this.viewStyle,
        ref: 'resize'
      }, this.$slots.default);
      const wrap = (
        <div
          ref="wrap"
          style={style}
          onScroll={this.handleScroll}
          class={[this.wrapClass, 'zm-scrollbar__wrap', gutter ? '' : 'zm-scrollbar__wrap--hidden-default']}>
          {[view]}
        </div>
      );
      let nodes;
      const stickyHeight = 8;

      if (!this.native) {
        if (this.sticky) {
          nodes = ([
            wrap,
            <zm-sticky
              ref="sticky"
              enabled={this.enabled}
              appendCls={this.appendCls}
              stickyHeight={stickyHeight}
              flow
            >
              <Bar
                ref="barX"
                move={this.moveX}
                size={this.sizeWidth}
                nativeOn-mouseenter={this.handleBarXEnter}
              ></Bar>
            </zm-sticky>,
            <Bar
              ref="barY"
              vertical
              move={this.moveY}
              size={this.sizeHeight}></Bar>
          ]);
        } else {
          nodes = ([
            wrap,
            <Bar
              ref="barX"
              move={this.moveX}
              size={this.sizeWidth}></Bar>,
            <Bar
              ref="barY"
              vertical
              move={this.moveY}
              size={this.sizeHeight}></Bar>
          ]);
        }
      } else {
        nodes = ([
          <div
            ref="wrap"
            class={[this.wrapClass, 'zm-scrollbar__wrap']}
            style={style}>
            {[view]}
          </div>
        ]);
      }
      return h('div', {
        class: ['zm-scrollbar', {
          'zm-scrollbar__focus': this.isFocus
        }],
        on: {
          mouseenter: this.focus,
          mouseleave: this.blur
        }
      }, nodes);
    },

    methods: {
      handleScroll() {
        const wrap = this.wrap;

        this.$emit('scroll', wrap);

        this.moveY = ((wrap.scrollTop * 100) / wrap.clientHeight);
        this.moveX = ((wrap.scrollLeft * 100) / wrap.clientWidth);

        const precisionValue = wrap.scrollHeight - Math.floor(wrap.scrollTop) - wrap.clientHeight;

        clearTimeout(this.scrollEndtimer);
        this.scrollEndtimer = setTimeout(() => {
          if (precisionValue >= 0 && precisionValue <= 1) {
            this.$emit('scroll-end', wrap);
          }
        }, 20);
      },

      update() {
        let heightPercentage, widthPercentage;
        const wrap = this.wrap;
        if (!wrap) return;

        heightPercentage = this.hack100(wrap.clientHeight * 100 / wrap.scrollHeight);
        widthPercentage = this.hack100(wrap.clientWidth * 100 / wrap.scrollWidth);

        this.sizeHeight = (heightPercentage < 100) ? (heightPercentage + '%') : '';
        this.sizeWidth = (widthPercentage < 100) ? (widthPercentage + '%') : '';
      },

      updateSticky() {
        if (this.sticky) {
          this.$refs.sticky.triggerScroll(true);
        }
      },

      hack100(p) {
        // hack for edge
        if (p >= 99.5) {
          p = 100;
        }
        return p;
      },

      scrollXToEndX() {
        this.$refs.barX.scrollToEnd();
      },

      scrollToEndY() {
        this.$refs.barY.scrollToEnd();
      },

      scrollToBeginX() {
        this.$refs.barX.scrollToBegin();
      },

      scrollToBeginY() {
        this.$refs.barY.scrollToBegin();
      },

      handleBarXEnter() {
        if (isInView(this.$slots.default[0].elm, document.body)) {
          this.focus();
        }
      },

      focus() {
        this.isFocus = true;
      },

      blur() {
        this.isFocus = false;
      },

      scrollIntoView(el, done, time = 300, offset = 0, isTop = false) {
        if (!el) {
          return;
        }
        const container = this.wrap;
        const height = container.clientHeight / 2;
        const offsetTop = getOffsetTop(el, this.$el);
        const topScrollTop = isTop ? offsetTop - offset : offsetTop - height - offset - 8;
        const scrollTop = Math.max(0, topScrollTop);
        const scroll = createScroll();
        scroll(container, 'scrollTop', scrollTop, time, () => {
          raf(() => {
            done && done();
          });
        });
      },

      refreshWrapStyl() {
        const gutter = scrollbarWidth();
        if (gutter) {
          const wrapperEl = this.$refs.wrap;
          const currentStyl = wrapperEl.getAttribute('style');
          wrapperEl.style = '';
          this._reflow = document.body.offsetHeight;
          wrapperEl.style = currentStyl;
        }
      }
    },

    mounted() {
      if (this.native) return;
      this.$nextTick(this.update);
      if (!this.noresize) {
        addResizeListener(this.$refs.wrap, this.update);
        addResizeListener(this.$refs.resize, this.update);
      }
    },

    beforeDestroy() {
      if (this.native) return;
      if (!this.noresize) {
        removeResizeListener(this.$refs.wrap, this.update);
        removeResizeListener(this.$refs.resize, this.update);
      }
    },

    activated() {
      this.wrap.scrollTop = this.moveY * this.wrap.clientHeight / 100;
      this.wrap.scrollLeft = this.moveX * this.wrap.clientWidth / 100;
    },

    watch: {
      enabled(newVal) {
        if (!this.sticky) {
          return;
        }
        newVal ? this.$refs.sticky.enable() : this.$refs.sticky.disable();
      },
      isFocus(newVal) {
        if (newVal) {
          this.updateSticky();
        }
      }
    }
  };
</script>

<style lang="scss">
  @import "../../scss/mixins/mixins";
  @import "../../scss/common/var";

  @include b(scrollbar) {
    overflow: hidden;
    position: relative;

    &:hover,
    &:active,
    &:focus {
      .zm-scrollbar__bar {
        opacity: 1;
        transition: opacity 340ms ease-out;
      }
    }

    &.zm-scrollbar__focus {
      .zm-scrollbar__bar {
        opacity: 1;
        transition: opacity 340ms ease-out;
      }
    }

    @include e(wrap) {
      overflow: scroll;
      height: 100%;

      @include m(hidden-default) {
        scrollbar-width: none;
        &::-webkit-scrollbar {
          width: 0;
          height: 0;
        }
      }
    }

    @include e(thumb) {
      position: relative;
      display: block;
      width: 0;
      height: 0;
      cursor: pointer;
      border-radius: inherit;
      background-color: $--color-border-secondary;
      transition: .3s background-color;

      &:hover {
        background-color: $--color-text-secondary;
      }
    }

    @include e(bar) {
      position: absolute;
      right: 2px;
      bottom: 2px;
      z-index: 10;
      border-radius: 8px;
      opacity: 0;
      transition: opacity 120ms ease-out;

      @include when(vertical) {
        width: 4px;
        top: 2px;

        > div {
          width: 100%;
        }
      }

      @include when(horizontal) {
        height: 6px;
        left: 2px;

        > div {
          height: 100%;
        }
      }

      @include when(mobile) {
        opacity: 1;
      }
    }
  }
</style>
